import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Cloud, Zap, Shield } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [humanVerified, setHumanVerified] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [checkboxTime, setCheckboxTime] = useState<number | null>(null);
  const navigate = useNavigate();
  const { login } = useAuth();

  // Countdown timer when checkbox is checked
  useEffect(() => {
    if (checkboxTime && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (checkboxTime && countdown === 0) {
      setHumanVerified(true);
    }
  }, [checkboxTime, countdown]);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setCheckboxTime(Date.now());
      setCountdown(5);
      setHumanVerified(false);
    } else {
      setCheckboxTime(null);
      setCountdown(5);
      setHumanVerified(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Bot detection: Check if human verification was completed
    if (!humanVerified || !checkboxTime) {
      setError('Please complete the human verification (wait for countdown)');
      return;
    }

    // Calculate time elapsed since checkbox was checked
    const timeElapsed = Date.now() - checkboxTime;

    // If less than 5 seconds elapsed, this is a bot
    if (timeElapsed < 5000) {
      setError('Bot detected! Please wait for the countdown to complete.');
      // Log bot attempt for fail2ban
      console.error('BOT ATTEMPT DETECTED - Login before countdown finished');
      return;
    }

    setLoading(true);

    try {
      // Send checkboxTime to backend for server-side validation
      await login(email, password, checkboxTime);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-black py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center">
            <div className="flex items-center space-x-3">
              <Cloud className="h-12 w-12 text-cyan-400" />
              <Zap className="h-12 w-12 text-amber-400" />
            </div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            Weather Ads Automation
          </h2>
          <p className="mt-2 text-center text-sm text-gray-300">
            Automatically adjust Google Ads bids based on weather
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email-address" className="sr-only">
                Email address
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-600 placeholder-gray-400 text-white bg-gray-800 rounded-t-md focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-600 placeholder-gray-400 text-white bg-gray-800 rounded-b-md focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 focus:z-10 sm:text-sm"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          {/* Human Verification Checkbox with Countdown */}
          <div className="rounded-md bg-gray-800/50 border border-cyan-600/30 p-4">
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="human-verify"
                  name="human-verify"
                  type="checkbox"
                  onChange={handleCheckboxChange}
                  className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-500 rounded bg-gray-700"
                />
              </div>
              <div className="ml-3 flex-1">
                <label htmlFor="human-verify" className="font-medium text-gray-200 flex items-center">
                  <Shield className="h-4 w-4 mr-2 text-cyan-400" />
                  I am not a robot
                </label>
                {checkboxTime && countdown > 0 && (
                  <p className="text-sm text-amber-400 mt-1 font-semibold animate-pulse">
                    Please wait: {countdown} second{countdown !== 1 ? 's' : ''}...
                  </p>
                )}
                {humanVerified && (
                  <p className="text-sm text-green-400 mt-1 font-semibold flex items-center">
                    ✓ Verified! You may now login
                  </p>
                )}
              </div>
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-900/30 border border-red-700 p-4">
              <div className="text-sm text-red-300">{error}</div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading || !humanVerified}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {loading ? 'Signing in...' : !humanVerified ? 'Complete verification first' : 'Sign in'}
            </button>
          </div>
        </form>

        <div className="text-center">
          <p className="text-xs text-gray-400">
            Powered by Open-Meteo Weather API
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Protected by fail2ban
          </p>
        </div>
      </div>
    </div>
  );
}
